import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import keycloak, { keycloakInitPromise } from './keycloak'
import { signOut } from './signOut'
import { restorePushAfterSignIn } from '../lib/push'
import { pickPrimaryRole, resolveActiveRole } from './rolePriority'
import { isStandaloneDisplay } from '../lib/pwaInstall'
import { canRenderPublicMarketing, hasOAuthResponse } from '../lib/publicMarketing'

interface UserInfo {
  sub: string
  email: string
  name: string
  role: string
}

interface AuthContextValue {
  authenticated: boolean
  initializing: boolean
  token: string | undefined
  userInfo: UserInfo | null
  roles: string[]
  activeRole: string
  setActiveRole: (role: string) => void
  refreshAuth: () => Promise<void>
  logout: () => void
  hasRole: (role: string) => boolean
}

const ACTIVE_ROLE_KEY = 'lupa_active_role'

const AuthContext = createContext<AuthContextValue>({
  authenticated: false,
  initializing: false,
  token: undefined,
  userInfo: null,
  roles: [],
  activeRole: '',
  setActiveRole: () => {},
  refreshAuth: async () => {},
  logout: () => {},
  hasRole: () => false,
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false)
  const [authenticated, setAuthenticated] = useState(false)
  // true between "slow-timer fired" and "init promise settled" — AuthGuard must not redirect during this window
  const [initializing, setInitializing] = useState(false)
  // The chosen cabinet for multi-role users. Roles themselves come from the (immutable) token;
  // activeRole only decides where the user lands and which nav is shown.
  const [activeRoleState, setActiveRoleState] = useState<string | null>(() => localStorage.getItem(ACTIVE_ROLE_KEY))
  // Bumping this re-renders so derived role data is recomputed from a freshly refreshed token.
  const [, bumpAuth] = useState(0)

  useEffect(() => {
    let settled = false

    // After 8 s without a response from Keycloak, unblock the UI in degraded mode.
    // The init promise keeps running — if KC responds later we still apply the result.
    const timer = setTimeout(() => {
      if (!settled) {
        setInitializing(true)
        setReady(true)
      }
    }, 8000)

    keycloakInitPromise
      .then((auth) => {
        settled = true
        clearTimeout(timer)
        setInitializing(false)
        setAuthenticated(auth)
        setReady(true)
      })
      .catch(() => {
        settled = true
        clearTimeout(timer)
        setInitializing(false)
        setReady(true)
      })

    // The access token expired while the tab is open. A refresh failure here is
    // NOT automatically a dead session: keycloak-js clears its tokens only when
    // the token endpoint answered 400 (refresh token expired/revoked) — that is
    // the only case worth a login redirect. Network blips keep the session and
    // retry on a timer plus immediately when the browser regains connectivity.
    let refreshRetryTimer: number | undefined
    const refreshExpiredToken = () => {
      clearTimeout(refreshRetryTimer)
      keycloak.updateToken(30).catch(() => {
        if (!keycloak.refreshToken) {
          keycloak.login()
          return
        }
        refreshRetryTimer = window.setTimeout(refreshExpiredToken, 15000)
      })
    }
    keycloak.onTokenExpired = refreshExpiredToken

    const onOnline = () => {
      if (keycloak.authenticated && keycloak.isTokenExpired()) refreshExpiredToken()
    }
    window.addEventListener('online', onOnline)

    // When the token is refreshed (e.g. after granting a new role) its roles may change —
    // re-render so derived role data reflects the new token.
    keycloak.onAuthRefreshSuccess = () => bumpAuth((v) => v + 1)

    return () => {
      clearTimeout(timer)
      clearTimeout(refreshRetryTimer)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  // Signing out unlinks this browser from the account's pushes (auth/signOut). Signing in links it
  // back — silently, and only where the browser already allows notifications and the account
  // wants them — so nobody has to open the profile to get their pushes back.
  useEffect(() => {
    if (authenticated) void restorePushAfterSignIn()
  }, [authenticated])

  // The public tracking page needs no sign-in: a customer on a weak network must not wait up to
  // 8 s for Keycloak before seeing the trip. It renders at once as a guest; if a session turns up
  // later, the page re-renders with it (the header then offers the cabinet).
  // Public marketing can do the same in a browser tab. Installed PWA startup and OAuth
  // callbacks keep the previous wait, so restoring a session still opens the cabinet.
  const publicView = isPublicTrackPath(window.location.pathname) || canRenderPublicMarketing(
    window.location.pathname,
    isStandaloneDisplay(),
    hasOAuthResponse(window.location.search, window.location.hash),
  )

  if (!ready && !publicView) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Загрузка...</p>
        </div>
      </div>
    )
  }

  const tp = keycloak.tokenParsed as Record<string, unknown> | undefined
  const roles: string[] = (tp?.realm_access as { roles?: string[] } | undefined)?.roles ?? []
  const primaryRole = pickPrimaryRole(roles)

  const userInfo: UserInfo | null = authenticated && tp
    ? {
        sub: String(tp.sub ?? ''),
        email: String(tp.email ?? ''),
        name: String(tp.name ?? tp.preferred_username ?? ''),
        role: primaryRole,
      }
    : null

  // Active cabinet: the stored choice if the user still holds that role, else their primary role.
  const activeRole = resolveActiveRole(roles, activeRoleState)

  const setActiveRole = (role: string) => {
    localStorage.setItem(ACTIVE_ROLE_KEY, role)
    setActiveRoleState(role)
  }

  // Force-refresh the access token so a freshly granted role appears in roles. Passing -1
  // forces a refresh regardless of token validity; onAuthRefreshSuccess triggers the re-render.
  const refreshAuth = async () => {
    try {
      await keycloak.updateToken(-1)
    } catch {
      /* keep current token on failure — caller handles UX */
    }
    bumpAuth((v) => v + 1)
  }

  const logout = () => {
    void signOut()
  }

  const hasRole = (role: string) => roles.includes(role)

  return (
    <AuthContext.Provider value={{ authenticated, initializing: initializing || !ready, token: keycloak.token, userInfo, roles, activeRole, setActiveRole, refreshAuth, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  )
}

/** Pages that work without Keycloak and must not wait for it. */
export function isPublicTrackPath(pathname: string): boolean {
  return pathname.startsWith('/track/')
}

export function useAuth() {
  return useContext(AuthContext)
}
